import { Fragment, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Skeleton, Table, THead, TBody, TR, TH, TD } from '../ui';
import { getPlanComparison, plansKeys, type ComparePlan, type CompareFeature } from '../../lib/api/plans.gen';

// Plan ↔ entitlement comparison matrix (story 2-4). The single pricing truth:
// rows are catalog features (grouped by category), columns are public plans, a
// check means the plan entitles that feature (plan.entitlements). Nothing is
// hand-maintained per plan — it renders from /plans/compare.
const priceLabel = (plan: ComparePlan): string => {
  if (plan.isCustom || plan.priceMonthly == null) return 'Custom';
  return plan.priceMonthly === 0 ? 'Free' : `$${plan.priceMonthly}/mo`;
};

export function PlanCompare(): JSX.Element | null {
  // Generated `plans.gen.ts` factory (G-19). One object, not a filtered
  // collection, so `.detail('compare')` rather than `.list(...)` — and the
  // SAME entry `PlatformConsolePage`'s owner-plane plan view reads (same
  // `GET /plans/compare` resource, just projected differently client-side).
  const { data, isLoading } = useQuery({
    queryKey: plansKeys.detail('compare'),
    queryFn: getPlanComparison,
    staleTime: 1000 * 60 * 10,
  });

  const grouped = useMemo(() => {
    const features: CompareFeature[] = data?.features ?? [];
    const order: string[] = [];
    const byCat = new Map<string, CompareFeature[]>();
    features.forEach((f) => {
      if (!byCat.has(f.category)) {
        byCat.set(f.category, []);
        order.push(f.category);
      }
      (byCat.get(f.category) as CompareFeature[]).push(f);
    });
    return order.map((cat) => [cat, byCat.get(cat) as CompareFeature[]] as const);
  }, [data]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data || data.plans.length === 0) return null;

  const entitled = data.plans.map((p) => new Set(p.entitlements));

  return (
    <div className="overflow-x-auto">
      <Table aria-label="Plan comparison">
        <THead>
          <TR>
            <TH>Feature</TH>
            {data.plans.map((plan) => (
              <TH key={plan.slug} className="text-center">
                <span className="block font-semibold text-foreground">{plan.name}</span>
                <span className="block text-xs font-normal text-muted-foreground">{priceLabel(plan)}</span>
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {grouped.map(([category, features]) => (
            <Fragment key={category}>
              <TR>
                <TD colSpan={data.plans.length + 1} className="bg-muted/40 text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                  {category}
                </TD>
              </TR>
              {features.map((feature) => (
                <TR key={feature.key}>
                  <TD className="text-foreground">{feature.name}</TD>
                  {data.plans.map((plan, i) => (
                    <TD key={plan.slug} className="text-center">
                      {entitled[i]?.has(feature.key) ? (
                        <CheckIcon className="mx-auto h-4 w-4 text-primary" aria-label="Included" />
                      ) : (
                        <span className="text-muted-foreground" aria-label="Not included">–</span>
                      )}
                    </TD>
                  ))}
                </TR>
              ))}
            </Fragment>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
